<script setup lang="ts">
// This component is a temp gallery slider for MVP ONLY (because image-previews are too small)
// it was made in 30 min and 'vue-gallery-slideshow' was used as inspiration
// TODO: please, implement something better, especially for mobile users (SC-62443)
import type { AppMedia } from '~/types/product.model'
const THUMBNAIL_WIDTH = 120

const props = defineProps<{ media: AppMedia[], index: null | number }>()
const emit = defineEmits<{
  close: []
}>()

const galleryRef = ref<HTMLDivElement>()
const imgIndex = ref(props.index)
const galleryXPos = ref(0)

const isMultiple = computed(() => props.media.length > 1)
const currMediaItem = computed(() =>
  imgIndex.value === null ? undefined : props.media[imgIndex.value],
)

const updateThumbnails = (): void => {
  if (!galleryRef.value || imgIndex.value === null) {
    return
  }

  const galleryWidth = galleryRef.value.clientWidth
  const currThumbsWidth = imgIndex.value * THUMBNAIL_WIDTH
  const maxThumbsWidth = props.media.length * THUMBNAIL_WIDTH
  const centerPos =
    Math.floor(galleryWidth / (THUMBNAIL_WIDTH * 2)) * THUMBNAIL_WIDTH

  if (maxThumbsWidth < galleryWidth) {
    return
  }

  if (currThumbsWidth < centerPos) {
    galleryXPos.value = 0
    return
  }

  if (
    currThumbsWidth >
    props.media.length * THUMBNAIL_WIDTH - galleryWidth + centerPos
  ) {
    galleryXPos.value = -(
      props.media.length * THUMBNAIL_WIDTH -
      galleryWidth -
      20
    )
    return
  }

  galleryXPos.value = -(imgIndex.value * THUMBNAIL_WIDTH) + centerPos
}

const close = (): void => {
  imgIndex.value = null
  emit('close')
}

const onPrev = (): void => {
  if (imgIndex.value === null) {
    return
  }
  if (imgIndex.value > 0) {
    imgIndex.value--
  } else {
    imgIndex.value = props.media.length - 1
  }
  updateThumbnails()
}

const onNext = (): void => {
  if (imgIndex.value === null) {
    return
  }
  if (imgIndex.value < props.media.length - 1) {
    imgIndex.value++
  } else {
    imgIndex.value = 0
  }
  updateThumbnails()
}

const onThumbClick = (index: number): void => {
  imgIndex.value = index
  updateThumbnails()
}

watch(
  () => props.index,
  (val, prev) => {
    useDisableBodyScroll(val !== null)
    imgIndex.value = val

    if (prev === null && val !== null) {
      nextTick(updateThumbnails)
    }
  },
  { immediate: true },
)

const keyDownHandler = ({ key }: KeyboardEvent): void => {
  if (key === 'ArrowLeft') {
    onPrev()
    return
  }
  if (key === 'ArrowRight') {
    onNext()
    return
  }
  if (key === 'Escape') {
    close()
  }
}

onMounted(() => {
  window.addEventListener('keydown', keyDownHandler)
})
onUnmounted(() => {
  window.removeEventListener('keydown', keyDownHandler)
})
</script>

<template>
  <transition name="modal">
    <div
      v-if="imgIndex !== null && media.length"
      class="image-viewer"
      @click="close"
    >
      <UiButton
        type="icon"
        class="image-viewer__close"
        @click="close"
      >
        <Icon name="fa6-solid:x" />
      </UiButton>
      <button
        v-if="isMultiple"
        type="button"
        class="image-viewer__prev"
        @click.stop="onPrev"
      >
        &lsaquo;
      </button>
      <div
        class="image-viewer__container"
        @click.stop="onNext"
      >
        <img
          class="image-viewer__img"
          :src="currMediaItem?.url"
          :alt="currMediaItem?.alt"
          @click.stop="onNext"
        >
      </div>
      <button
        v-if="isMultiple"
        type="button"
        class="image-viewer__next"
        @click.stop="onNext"
      >
        &rsaquo;
      </button>
      <div
        v-if="isMultiple"
        ref="galleryRef"
        class="image-viewer__gallery"
      >
        <div class="image-viewer__gallery-title">
          {{ imgIndex + 1 }} / {{ media.length }}
        </div>
        <div
          class="image-viewer__gallery-container"
          :style="{ transform: 'translate(' + galleryXPos + 'px, 0)' }"
        >
          <img
            v-for="(img, i) in media"
            :key="`${i}-${img.alt}`"
            :class="[
              'image-viewer__gallery-img',
              { '--active': i === imgIndex },
            ]"
            :src="img.url"
            :alt="img.alt"
            @click.stop="onThumbClick(i)"
          >
        </div>
      </div>
    </div>
  </transition>
</template>

<style lang="scss" scoped>
$radius-medium: 8px;
$screen-xs-max: 767px;
$no-gallery-height: 588px;

// I am not joking, this is hotjar z-index
$hotjar-z-index: 2147483640;

@mixin modal-mixin {
  transition: opacity 0.2s ease;
  position: fixed;
  z-index: $hotjar-z-index + 1;
  top: 0;
  left: 0;
  height: 100vh;
  background-color: var(--app-black-alpha-80);

  // fallback, when calc() is not supported
  width: 100%;
  min-height: 100%;

  // calc(100% + 1px) is a fix for some screen sizes,
  // which have visible 1px on the sides of backdrop
  /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
  width: calc(100% + 1px);
  /* stylelint-disable-next-line declaration-block-no-duplicate-properties */
  min-height: calc(100% + 1px);
}

.image-viewer {
  @include modal-mixin;

  touch-action: manipulation;

  &__close {
    color: var(--app-color-sendcloud-white);
    position: absolute;
    top: 0;
    right: 0;
    background-color: transparent;
    border: none;
    font-size: 20px;
    width: 50px;
    height: 50px;
    cursor: pointer;
    z-index: $hotjar-z-index + 2;

    &:focus {
      outline: 0;
    }
  }

  &__prev {
    left: 0;
    text-align: left;
  }

  &__next {
    right: 0;
    text-align: right;
  }

  &__prev,
  &__next {
    /* stylelint-disable-next-line color-function-notation */
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 40%);
    position: absolute;
    width: 45vw;
    height: 45vh;
    top: 50%;
    padding: var(--app-spacer-14);
    transform: translateY(-50%);
    z-index: 999;
    cursor: pointer;
    font-size: 40px;
    color: var(--app-color-sendcloud-white);
    background-color: transparent;
    border: none;

    &:focus {
      outline: 0;
    }
  }
}

.image-viewer__container {
  -webkit-tap-highlight-color: transparent;
  position: absolute;
  cursor: pointer;
  overflow: hidden;
  max-width: 100vh;
  margin: var(--app-spacer-8) auto 0;
  top: 5vh;
  left: var(--app-spacer-8);
  right: var(--app-spacer-8);
  height: 70vh;

  @media (max-width: $screen-xs-max), (max-height: $no-gallery-height) {
    width: 100%;
    max-width: 100%;
    top: 50%;
    transform: translateY(-50%);
    left: 0;
    right: 0;
    border-radius: 0;
    min-height: 280px;
    max-height: 95vh;
  }
}

.image-viewer__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.image-viewer__gallery {
  @media (max-width: $screen-xs-max), (max-height: $no-gallery-height) {
    display: none;
  }

  overflow-x: hidden;
  overflow-y: hidden;
  position: absolute;
  bottom: 10px;
  margin: auto;
  max-width: 100vh;
  white-space: nowrap;
  left: var(--app-spacer-8);
  right: var(--app-spacer-8);
}

.image-viewer__gallery-title {
  color: var(--app-color-sendcloud-white);
  margin-bottom: var(--app-spacer-8);
}

.image-viewer__gallery-container {
  overflow: visible;
  display: block;
  height: 100px;
  white-space: nowrap;
  /* stylelint-disable-next-line plugin/no-low-performance-animation-properties */
  transition: all 200ms ease-in-out;
  width: 100%;
}

.image-viewer__gallery-img {
  width: 100px;
  height: 100px;
  object-fit: cover;
  display: inline-block;
  margin-right: 20px;
  cursor: pointer;
  opacity: 0.6;
  border-radius: $radius-medium;

  &.--active {
    width: 100px;
    display: inline-block;
    opacity: 1;
  }
}

.modal-enter {
  opacity: 0;
}

.modal-leave-active {
  opacity: 0;
}
</style>
